import React, { useContext, useEffect, useRef, useState } from "react";
import { Puff } from "react-loading-icons";
import { CartContext } from "../../CartContext";

const checkoutUrl =
  "https://stripe-server-reena-36b70709cadc.herokuapp.com/create-checkout-session";

const parseCartPrice = (price) => {
  if (typeof price === "number") return Number.isFinite(price) ? price : null;
  if (typeof price !== "string") return null;

  const match = price.replace(/,/g, "").match(/\d+(\.\d+)?/);
  return match ? Number(match[0]) : null;
};

const formatCartCurrency = (amount) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);

const getCartImage = (item) =>
  Array.isArray(item.image) ? item.image[0] : item.image;

const getLinePrice = (item) => {
  const price = parseCartPrice(item.price);
  return price === null ? null : price * item.quantity;
};

const getCartSubtotal = (cartItems) =>
  cartItems.reduce((total, item) => total + (getLinePrice(item) || 0), 0);

const CartDrawer = () => {
  const { cart, removeFromCart, isCartOpen, closeCart, toggleCart } =
    useContext(CartContext);
  const [loading, setLoading] = useState(false);
  const cartRef = useRef(null);
  const cartSubtotal = getCartSubtotal(cart);
  const cartCount = cart.reduce((total, item) => total + item.quantity, 0);

  useEffect(() => {
    if (!isCartOpen) return undefined;

    const handleClickOutside = (event) => {
      if (cartRef.current && !cartRef.current.contains(event.target)) {
        closeCart();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [closeCart, isCartOpen]);

  async function handleSubmit(event) {
    event.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(checkoutUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cart }),
      });

      if (!response.ok) throw new Error(response.statusText);
      const session = await response.json();
      window.location.href = session.url;
    } catch (error) {
      console.error("Error", error);
      alert("There was an error processing your request. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      {loading && (
        <div className="cart-loading" role="status" aria-live="polite">
          <Puff stroke="#111111" />
          <span>Redirecting to Stripe</span>
        </div>
      )}

      {!isCartOpen ? (
        <div className="fixed-bottom-right-container">
          <button
            type="button"
            className="fixed-bottom-right-button"
            onClick={toggleCart}
          >
            Review Cart{cartCount > 0 ? ` (${cartCount})` : ""}
          </button>
        </div>
      ) : (
        <aside className="expanded-cart" ref={cartRef} aria-label="Shopping cart">
          <div className="header">
            <h5>Your Cart</h5>
            <button
              className="cart-close-button"
              type="button"
              onClick={closeCart}
              aria-label="Close cart"
            >
              Close
            </button>
          </div>

          <div className="cart-items">
            {cart.length === 0 ? (
              <p className="cart-empty-message">Your cart is currently empty.</p>
            ) : (
              cart.map((item, index) => (
                <div className="cart-line-item" key={`${item.id}-${item.size}-${index}`}>
                  <p>Item: {item.name}</p>
                  {item.size && <p>Size: {item.size}</p>}
                  <p>Qty: {item.quantity}</p>
                  <p className="cart-line-price">
                    {getLinePrice(item) === null
                      ? "Price available at checkout"
                      : `${formatCartCurrency(getLinePrice(item))} USD`}
                  </p>
                  <img
                    src={getCartImage(item)}
                    height={90}
                    width={90}
                    alt={item.name}
                  />
                  <button
                    className="cart-remove-button"
                    type="button"
                    onClick={() => removeFromCart(item.id, item.size)}
                  >
                    Remove
                  </button>
                </div>
              ))
            )}

            <form onSubmit={handleSubmit}>
              <div className="cart-summary">
                <span>Order Subtotal</span>
                <strong>{formatCartCurrency(cartSubtotal)} USD</strong>
              </div>
              <p className="cart-checkout-note">
                Shipping costs will be calculated at checkout. You&apos;ll be
                redirected to Stripe to complete payment securely.
              </p>
              <div className="cart-buttons">
                <button
                  className="checkout-buttons2 checkout-buttons2--stripe"
                  type="submit"
                  disabled={cart.length === 0}
                  aria-label="Secure checkout with Stripe payment portal"
                >
                  <span className="checkout-stripe-label">
                    Secure checkout with{" "}
                    <span className="checkout-stripe-name">Stripe</span>
                  </span>
                </button>
                <button
                  className="checkout-buttons2"
                  type="button"
                  onClick={closeCart}
                >
                  Close
                </button>
              </div>
            </form>
          </div>
        </aside>
      )}
    </>
  );
};

export default CartDrawer;
